import { DEFAULTS, STORAGE_KEYS } from './userStorage.shared'

export { DEFAULTS, STORAGE_KEYS }

// Define UserPreferences interface locally
export interface UserPreferences {
  name?: string | null
  timezone?: string | null
  dayStart?: string
  dayEnd?: string
  phone?: string | null
  proactiveFrequency?: string
  isSmsNotificationEnabled?: boolean
}

/**
 * Gets stored preferences from session storage
 */
export const getStoredPreferences = async (): Promise<UserPreferences | null> => {
  try {
    const getValue = (key: keyof typeof STORAGE_KEYS) => {
      const storageKey = STORAGE_KEYS[key]
      const value = window.sessionStorage.getItem(storageKey)
      return value === null || value === '' ? null : value
    }

    const dayStart = getValue('DAY_START') ?? DEFAULTS.DAY_START
    const dayEnd = getValue('DAY_END') ?? DEFAULTS.DAY_END

    const prefs: UserPreferences = {
      name: getValue('NAME'),
      timezone: getValue('TIMEZONE'),
      dayStart,
      dayEnd,
      phone: getValue('PHONE'),
      proactiveFrequency: getValue('PROACTIVE_FREQUENCY') ?? DEFAULTS.PROACTIVE_FREQUENCY,
      isSmsNotificationEnabled: getValue('IS_SMS_NOTIFICATION_ENABLED') === 'true',
    }

    return prefs
  } catch (e) {
    console.error('[userStorage] getStoredPreferences error:', e)
    return null
  }
}

/**
 * Sets user preferences in session storage
 */
export const setStoredPreferences = async (prefs: UserPreferences): Promise<void> => {
  try {
    // Explicit mapping from camelCase keys to STORAGE_KEYS keys
    const keyMapping: Record<keyof UserPreferences, keyof typeof STORAGE_KEYS> = {
      name: 'NAME',
      timezone: 'TIMEZONE',
      dayStart: 'DAY_START',
      dayEnd: 'DAY_END',
      phone: 'PHONE',
      proactiveFrequency: 'PROACTIVE_FREQUENCY',
      isSmsNotificationEnabled: 'IS_SMS_NOTIFICATION_ENABLED',
    }

    Object.entries(prefs).forEach(([key, value]) => {
      if (value === undefined || value === null) {
        return
      }

      const mappedKey = keyMapping[key as keyof UserPreferences]
      if (!mappedKey) {
        return
      }

      // Lookup the proper storage key from STORAGE_KEYS
      const storageKey = STORAGE_KEYS[mappedKey]
      if (!storageKey) {
        return
      }

      window.sessionStorage.setItem(storageKey, String(value))
    })
  } catch (e) {
    console.error('[userStorage] setStoredPreferences error:', e)
  }
}
